import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { PopupComponent } from '@libs/front/components/popup/popup.component';
import { ButtonComponent } from '@libs/front/components/button/button.component';
import { CheckboxComponent } from '@libs/front/components/checkbox/checkbox.component';
import { InputComponent } from '@libs/front/components/input/input.component';
import { CalendarComponent } from '@libs/front/components/calendar/calendar.component';
import { SelectComponent, SelectOption as LibSelectOption } from '@libs/front/components/select/select.component';
import {
  DataTableComponent,
  TableCellDirective,
  TableColumn,
  TableHeaderDirective,
} from '@libs/front/components/data-table/data-table.component';
import { ScrollableComponent } from 'src/app/shared/components/scrollable/scrollable.component';

import { ControlPoint } from 'src/app/bo/controlPoint';
import { AssignedRecords } from 'src/app/bo/vibrationsForChart/assigned-records';
import { VibrationData } from 'src/app/bo/vibrationsForChart/vibration-data';
import { FonctionsService } from 'src/app/services/fonctions-partagees.service';
import { Blast, HoleService } from 'src/app/services/hole.service';
import { VibrationsRecordService } from 'src/app/services/vibrations-record.service';
import { toPreviewDataUrl } from '../vibration-preview.util';

/** Ligne du tableau : propriétés plates pour que le tri natif du tableau fonctionne. */
interface AssignRow {
  id: number;
  record: VibrationData;
  fileName: string;
  timestampMs: number;
  dateLabel: string;
  timeLabel: string;
  notes: string;
  note1: string;
  note2: string;
  note3: string;
  vAbs: number | null;
  lAbs: number | null;
  tAbs: number | null;
  vecSomme: number | null;
  /** Clé de tri de la colonne « Maximum ». */
  maxPpv: number;
  controlPointName: string;
  deviceLabel: string;
  previews: (string | null)[];
}

const PAGE_SIZE = 30;

/** Un enregistrement ne dépasse jamais 4 canaux (3 axes de géophone + 1 micro). */
const MAX_CHANNELS = 4;

/**
 * Refonte de `app-assign-records-modal` : même logique (filtres serveur, pagination au
 * scroll, sélection multiple, calcul de la distance au tir puis affectation), rendue avec
 * les composants génériques de `@libs/front/components`.
 */
@Component({
  selector: 'app-blast-assign-records-modal',
  templateUrl: './blast-assign-records-modal.component.html',
  styleUrls: ['./blast-assign-records-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    PopupComponent,
    ButtonComponent,
    CheckboxComponent,
    InputComponent,
    CalendarComponent,
    SelectComponent,
    ScrollableComponent,
    DataTableComponent,
    TableCellDirective,
    TableHeaderDirective,
  ],
})
export class BlastAssignRecordsModalComponent {
  readonly isOpen = input<boolean>(false);
  readonly siteId = input.required<number>();
  readonly blastId = input<number>();
  readonly tirName = input<string>('');
  /** Date de tir réelle, sinon planifiée : pré-remplit le filtre de dates */
  readonly tirDate = input<Date | null>(null);

  readonly closed = output<void>();
  /** Émis après une affectation réussie : le parent recharge sa liste. */
  readonly assigned = output<void>();

  private readonly scrollable = viewChild(ScrollableComponent);

  // ── État ───────────────────────────────────────────────────────────────────

  protected readonly records = signal<VibrationData[]>([]);
  protected readonly controlPoints = signal<ControlPoint[]>([]);
  protected readonly devices = signal<any[]>([]);
  protected readonly selectedRecordIds = signal<number[]>([]);
  protected readonly selectedControlPointId = signal<number | null>(null);
  protected readonly errorMessage = signal('');
  protected readonly selectedChannelIndex = signal(0);

  protected readonly serialNumberFilter = signal<string[]>([]);
  protected readonly startDateFilter = signal('');
  protected readonly endDateFilter = signal('');
  protected readonly noteFilter = signal('');

  private currentPage = 1;
  private isLoading = false;
  private initialized = false;
  protected readonly noteSearch$ = new Subject<string>();

  private readonly recordSevice = inject(VibrationsRecordService);
  private readonly fonctionsService = inject(FonctionsService);
  private readonly translate = inject(TranslateService);
  private readonly holeService = inject(HoleService);

  // ── Options des sélecteurs (première entrée = réinitialisation) ────────────

  protected readonly deviceOptions = computed<LibSelectOption[]>(() =>
    this.devices().map(d => ({ id: d?.serialNumber ?? d, label: d?.serialNumber ?? d })),
  );

  protected readonly controlPointOptions = computed<LibSelectOption[]>(() => [
    { id: '', label: '—' },
    ...this.controlPoints().map(cp => ({ id: cp.controlPointId, label: cp.name })),
  ]);

  protected readonly rows = computed<AssignRow[]>(() => this.records().map(record => this.toRow(record)));

  protected readonly maxChannelCount = computed(() =>
    this.rows().reduce(
      (max, row) => Math.max(max, Math.min(row.record.recordMetadata?.transducerAxis?.length || 3, MAX_CHANNELS)),
      1,
    ),
  );

  protected readonly tableColumns: TableColumn[] = [
    { key: 'select', label: '', sortable: false, width: '2.25rem' },
    { key: 'channel', label: 'VIBRATION.CHANNEL', sortable: false, width: '13%' },
    { key: 'fileName', label: 'VIBRATION.FILE_NAME', width: '16%' },
    { key: 'timestampMs', label: 'VIBRATION.DATE_TIME', width: '14%' },
    { key: 'notes', label: 'VIBRATION.NOTE', width: '13%' },
    { key: 'maxPpv', label: 'VIBRATION.MAX', width: '16%' },
    { key: 'controlPointName', label: 'VIBRATION.ASSIGNED' },
  ];

  constructor() {
    // Chargement à la première ouverture, comme le dialog qui n'existait qu'ouvert
    effect(() => {
      if (!this.isOpen() || this.initialized) return;
      this.initialized = true;
      this.initDateFilters();
      this.loadControlPoints();
      this.getDevicesNames();
      this.loadInitialRecords();
    });

    this.noteSearch$
      .pipe(debounceTime(500), takeUntilDestroyed())
      .subscribe(() => this.onFilterChange());
  }

  // ── Chargement ─────────────────────────────────────────────────────────────

  // Start = date du tir, end = start + 1 jour, au format yyyy-MM-dd attendu par app-calendar
  private initDateFilters(): void {
    const tirDate = this.tirDate();
    if (!tirDate) return;
    const start = new Date(tirDate);
    const end = new Date(start);
    end.setDate(end.getDate() + 1);
    this.startDateFilter.set(this.toIsoDate(start));
    this.endDateFilter.set(this.toIsoDate(end));
  }

  private toIsoDate(d: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }

  private loadInitialRecords(): void {
    this.isLoading = true;
    const startDate = this.startDateFilter() || null;
    const endDate = this.endDateFilter() || null;
    this.recordSevice.GetRecords(this.siteId(), null, null, null, 1, PAGE_SIZE, false, true, true, startDate, endDate).subscribe({
      next: (response) => {
        // Seuls les enregistrements non encore affectés sont proposés
        this.records.set((response.data?.records ?? []).filter(r => r.controlPointId == null || r.blastId == null));
        this.isLoading = false;
      },
      error: () => {
        this.records.set([]);
        this.isLoading = false;
        this.fonctionsService.launch_toast(false, this.translate.instant('ERRORS_MESSAGE.LOADING_FAILED'));
      },
    });
  }

  private getDevicesNames(): void {
    this.recordSevice.getDeviceNames().subscribe({
      next: (devicesNames) => this.devices.set(devicesNames ?? []),
      error: () => {
        this.devices.set([]);
        this.fonctionsService.launch_toast(false, this.translate.instant('ERRORS_MESSAGE.LOADING_FAILED'));

      },
    });
  }

  private loadControlPoints(): void {
    this.recordSevice.loadControlPoints(this.siteId()).subscribe({
      next: (response) => this.controlPoints.set(response ?? []),
      error: () => {
        this.controlPoints.set([]);
        this.fonctionsService.launch_toast(false, this.translate.instant('ERRORS_MESSAGE.LOADING_FAILED'));
      },
    });
  }

  private loadRecords(pageNumber = 1, append = false): void {
    if (this.isLoading) return;
    this.isLoading = true;

    const serialNumbers = this.serialNumberFilter();
    const serialNumber = serialNumbers.length ? serialNumbers : null;
    const startDate = this.startDateFilter() || null;
    const endDate = this.endDateFilter() || null;
    const note = this.noteFilter() || null;

    this.recordSevice
      .GetRecords(this.siteId(), null, null, serialNumber, pageNumber, PAGE_SIZE, false, true, true, startDate, endDate, note)
      .subscribe({
        next: (response) => {
          const page = response.data?.records ?? [];
          if (append) this.records.update(curr => [...curr, ...page]);
          else this.records.set(page);
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
                  this.fonctionsService.launch_toast(false, this.translate.instant('ERRORS_MESSAGE.LOADING_FAILED'));

        },
      });
  }

  // ── Filtres / pagination ───────────────────────────────────────────────────

  protected onFilterChange(): void {
    this.currentPage = 1;
    this.scrollable()?.scrollToTop();
    this.loadRecords();
  }

  protected onSerialNumbersSelected(options: LibSelectOption[]): void {
    this.serialNumberFilter.set(options.map(opt => opt.id as string));
    this.onFilterChange();
  }

  protected onStartDateChange(value: string): void {
    this.startDateFilter.set(value ?? '');
    this.onFilterChange();
  }

  protected onEndDateChange(value: string): void {
    this.endDateFilter.set(value ?? '');
    this.onFilterChange();
  }

  protected onNoteInput(value: string): void {
    this.noteFilter.set(value ?? '');
    this.noteSearch$.next(this.noteFilter());
  }

  /** Pagination : page suivante quand la liste approche du bas. */
  protected onScrolledToEnd(): void {
    if (this.isLoading) return;
    this.currentPage++;
    this.loadRecords(this.currentPage, true);
  }

  // ── Sélection ──────────────────────────────────────────────────────────────

  protected isSelected(recordId: number): boolean {
    return this.selectedRecordIds().includes(recordId);
  }

  protected toggleSelect(recordId: number): void {
    this.selectedRecordIds.update(ids =>
      ids.includes(recordId) ? ids.filter(id => id !== recordId) : [...ids, recordId],
    );
    if (this.errorMessage()) this.errorMessage.set('');
  }

  protected onControlPointSelected(option: LibSelectOption): void {
    const id = option?.id;
    this.selectedControlPointId.set(id === '' || id == null ? null : Number(id));
  }

  protected cycleChannel(dir: number): void {
    const max = this.maxChannelCount();
    this.selectedChannelIndex.set((this.selectedChannelIndex() + dir + max) % max);
  }

  protected previewFor(row: AssignRow, channelIndex: number): string | null {
    return row.previews[channelIndex] ?? null;
  }

  // ── Validation ─────────────────────────────────────────────────────────────

  protected closeDialog(): void {
    this.closed.emit();
  }

  protected closeErrorMessage(): void {
    this.errorMessage.set('');
  }

  protected confirm(): void {
    if (this.selectedRecordIds().length === 0) {
      this.errorMessage.set(this.translate.instant('ERRORS_MESSAGE.SELECT_RECORDS_ERROR'));
      return;
    }

    const controlPoint = this.controlPoints().find(cp => cp.controlPointId === this.selectedControlPointId());
    const blastId = this.blastId();
    if (controlPoint && blastId) {
      this.holeService.GetBlastHoles(blastId).then((blast: Blast) => {
        const distance = this.holeService.computeMinDistanceToHoles(controlPoint, blast.Holes ?? []);
        this.submitAssignment(controlPoint, distance);
      });
    } else {
      this.submitAssignment(null, null);
    }
  }

  private submitAssignment(controlPoint: ControlPoint | null, distance: number | null): void {
    const data: AssignedRecords = {
      recordIds: this.selectedRecordIds(),
      eventId: null,
      controlPointId: this.selectedControlPointId(),
      blastId: this.blastId()!,
      siteId: this.siteId(),
      controlPointName: controlPoint?.name,
      distance: distance ?? undefined,
    };

    this.recordSevice.assignRecords(this.siteId(), data).subscribe({
      next: () => {
        this.fonctionsService.launch_toast(true);
        this.assigned.emit();
        this.closed.emit();
      },
      error: () => {
        this.fonctionsService.launch_toast(false, this.translate.instant('RECORD.ASSIGNMENT_ERROR'));
      },
    });
  }

  // ── View model ─────────────────────────────────────────────────────────────

  private toRow(record: VibrationData): AssignRow {
    const meta = record.recordMetadata;
    const [dateLabel, timeLabel] = this.formatTimestamp(meta?.timestamp);

    return {
      id: record.id,
      record,
      fileName: meta?.fileName ?? '',
      timestampMs: meta?.timestamp ? new Date(meta.timestamp).getTime() : 0,
      dateLabel,
      timeLabel,
      notes: [meta?.note1, meta?.note2, meta?.note3].filter(Boolean).join(' '),
      note1: meta?.note1 ?? '',
      note2: meta?.note2 ?? '',
      note3: meta?.note3 ?? '',
      vAbs: meta?.verticalPpv ?? null,
      lAbs: meta?.longitudinalPpv ?? null,
      tAbs: meta?.transversePpv ?? null,
      vecSomme: meta?.vecSomme ?? null,
      maxPpv: Math.max(
        Math.abs(meta?.verticalPpv ?? 0),
        Math.abs(meta?.longitudinalPpv ?? 0),
        Math.abs(meta?.transversePpv ?? 0),
      ),
      controlPointName: this.getControlPointName(record.controlPointId),
      deviceLabel: meta?.seismographName ?? meta?.seismographSerialNumber ?? '',
      previews: [
        toPreviewDataUrl(meta?.channel1PreviewImage),
        toPreviewDataUrl(meta?.channel2PreviewImage),
        toPreviewDataUrl(meta?.channel3PreviewImage),
      ],
    };
  }

  protected getControlPointName(controlPointId?: number): string {
    const controlPoints = this.controlPoints();
    if (!controlPoints.length || !controlPointId) return '';
    return controlPoints.find(cp => cp.controlPointId === controlPointId)?.name ?? '';
  }

  public findMaxAbsValue(min: number, max: number): number {
    return Math.abs(max) > Math.abs(min) ? max : Math.abs(min);
  }

  /**
   * Découpe l'horodatage tel qu'il est renvoyé par le back, sans passer par `Date` :
   * aucune conversion de fuseau, la date et l'heure affichées sont celles reçues.
   */
  private formatTimestamp(ts?: string): [string, string] {
    const parts = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/.exec(ts ?? '');
    if (!parts) return ['-', '-'];
    const [, year, month, day, hours, minutes, seconds] = parts;
    return [`${day}/${month}/${year}`, `${hours}:${minutes}:${seconds ?? '00'}`];
  }
}
